import { useState, useEffect, useRef } from 'react';
import { User, FileSpreadsheet, Download, Upload, Copy, Trash2, Building2, Phone, Mail, Bell, BellOff, AlertTriangle, Sun, Moon, Cloud, CloudOff, RefreshCw, Loader2, Camera, LogOut, MapPin, Clock, Globe, Navigation } from 'lucide-react';
import { showToast } from './Toast';
import { requestNotificationPermission, areNotificationsEnabled, getReminderSettings, saveReminderSettings, getStoresNeedingRevisit, getNotificationPermissionStatus } from '../utils/notifications';
import { formatPhoneInput } from '../utils/phone';
import { useTheme } from '../hooks/useTheme';
import { exportToCSV, exportToJSON, downloadBlob } from './ConfigTabComponents';
import { geocodeAddress } from '../utils/geocode';

const DEFAULT_AVATAR = 'https://images.pexels.com/photos/6146051/pexels-photo-6146051.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=200&w=200';
const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ConfigTab({ lojas, setLojas, profile, setProfile, syncStatus, lastSyncTime, backendAvailable, user, onLogout, saveToFirestore }) {
  const [localProfile, setLocalProfile] = useState(profile);
  const [reminderSettings, setReminderSettings] = useState(getReminderSettings());
  const [notificationsGranted, setNotificationsGranted] = useState(areNotificationsEnabled());
  const [notificationStatus, setNotificationStatus] = useState(getNotificationPermissionStatus());
  const overdueStores = getStoresNeedingRevisit(lojas, reminderSettings.days);
  const { isDark, toggleTheme } = useTheme();
  
  // Photo upload state
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [uploadedPhotoUrl, setUploadedPhotoUrl] = useState(null);
  const photoInputRef = useRef(null);
  
  // Geocoding state
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodeProgress, setGeocodeProgress] = useState({ current: 0, total: 0 });
  
  // Derive avatar URL from profile or uploaded photo
  const avatarUrl = uploadedPhotoUrl || 
    (profile.photoUrl) || 
    (profile.photoId ? `${API_URL}/api/photos/${profile.photoId}` : DEFAULT_AVATAR);

  useEffect(() => {
    setLocalProfile(profile);
  }, [profile]);

  const handleProfileChange = (field, value) => {
    const updated = { ...localProfile, [field]: value };
    setLocalProfile(updated);
    setProfile(updated);
  };

  // Re-geocode all stores
  const handleRegeocodeAll = async () => {
    if (isGeocoding) return;
    
    const storesWithAddress = lojas.filter(l => l.address && l.city);
    if (storesWithAddress.length === 0) {
      showToast('Nenhuma loja com endereço para geocodificar', 'warning');
      return;
    }
    
    setIsGeocoding(true);
    setGeocodeProgress({ current: 0, total: storesWithAddress.length });
    
    let updatedLojas = [...lojas];
    let successCount = 0;
    let failCount = 0;
    
    for (let i = 0; i < storesWithAddress.length; i++) {
      const loja = storesWithAddress[i];
      setGeocodeProgress({ current: i + 1, total: storesWithAddress.length });
      
      try {
        const result = await geocodeAddress(
          loja.address,
          loja.city,
          loja.state,
          loja.zip
        );
        
        if (result.success) {
          updatedLojas = updatedLojas.map(l => 
            l.id === loja.id 
              ? { ...l, lat: result.lat, lng: result.lng, updatedAt: new Date().toISOString() }
              : l
          );
          successCount++;
          console.log(`[Geocode] ${loja.name}: ${result.lat}, ${result.lng}`);
        } else {
          failCount++;
          console.log(`[Geocode] Failed for ${loja.name}: ${result.error}`);
        }
      } catch (error) {
        failCount++;
        console.error(`[Geocode] Error for ${loja.name}:`, error);
      }
      
      // Small delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    
    // Update state and save to Firestore
    setLojas(updatedLojas);
    if (saveToFirestore) {
      await saveToFirestore(updatedLojas, profile);
    }
    
    setIsGeocoding(false);
    setGeocodeProgress({ current: 0, total: 0 });
    
    if (successCount > 0) {
      showToast(`${successCount} lojas atualizadas com sucesso!${failCount > 0 ? ` (${failCount} falharam)` : ''}`, 'success');
    } else {
      showToast('Não foi possível atualizar as coordenadas', 'error');
    }
  };

  // Handle photo upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      showToast('Tipo de arquivo inválido. Use JPG, PNG, GIF ou WebP.', 'error');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showToast('Arquivo muito grande. Máximo: 5MB', 'error');
      return;
    }
    
    setIsUploadingPhoto(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/upload/photo`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload falhou');
      }
      
      const data = await response.json();
      
      // Update profile with photo ID
      const photoUrl = `${API_URL}/api/photos/${data.photoId}`;
      setUploadedPhotoUrl(photoUrl);
      handleProfileChange('photoId', data.photoId);
      handleProfileChange('photoUrl', photoUrl);
      
      showToast('Foto atualizada com sucesso!');
    } catch (error) {
      console.error('Photo upload error:', error);
      showToast(error.message || 'Erro ao fazer upload', 'error');
    } finally {
      setIsUploadingPhoto(false);
      // Reset input
      if (photoInputRef.current) {
        photoInputRef.current.value = '';
      }
    }
  };

  // Handle phone input with US formatting
  const handlePhoneChange = (value) => {
    const formatted = formatPhoneInput(value);
    handleProfileChange('phone', formatted);
  };

  const handleEnableNotifications = async () => {
    const result = await requestNotificationPermission();
    setNotificationsGranted(result.granted);
    setNotificationStatus(getNotificationPermissionStatus());
    if (result.granted) {
      showToast('Notifications enabled!');
      setReminderSettings(prev => {
        const updated = { ...prev, enabled: true };
        saveReminderSettings(updated);
        return updated;
      });
    } else {
      showToast('Notification permission denied', 'error');
    }
  };

  const handleToggleReminders = () => {
    const updated = { ...reminderSettings, enabled: !reminderSettings.enabled };
    setReminderSettings(updated);
    saveReminderSettings(updated);
    showToast(updated.enabled ? 'Reminders enabled' : 'Reminders disabled');
  };

  const handleReminderDaysChange = (days) => {
    const updated = { ...reminderSettings, days: parseInt(days) };
    setReminderSettings(updated);
    saveReminderSettings(updated);
  };

  // Handle logout
  const handleLogout = () => {
    if (window.confirm('Deseja sair da sua conta?')) {
      onLogout();
    }
  };

  // Export to CSV (Excel compatible with BOM)
  const handleExportCSV = () => {
    exportToCSV(lojas);
    showToast('CSV exportado com sucesso!');
  };

  // Export to JSON
  const handleExportJSON = () => {
    exportToJSON(lojas);
    showToast('JSON exportado com sucesso!');
  };

  // Copy raw JSON
  const handleCopyJSON = async () => {
    const data = JSON.stringify(lojas, null, 2);
    try {
      await navigator.clipboard.writeText(data);
      showToast('JSON copiado!');
    } catch (err) {
      showToast('Erro ao copiar', 'error');
    }
  };

  // Import JSON
  const handleImportJSON = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target?.result);
        if (!Array.isArray(data)) {
          showToast('Invalid format', 'error');
          return;
        }
        if (window.confirm(`Import ${data.length} stores? This will replace current data.`)) {
          setLojas(data);
          showToast(`${data.length} stores imported!`);
        }
      } catch (err) {
        showToast('Error reading file', 'error');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // Delete all data
  const handleDeleteAll = () => {
    if (window.confirm('Are you sure you want to delete ALL data?')) {
      if (window.confirm('This action is IRREVERSIBLE. Confirm deletion?')) {
        setLojas([]);
        showToast('All data deleted');
      }
    }
  };

  return (
    <div className="flex flex-col gap-6 pb-20">
      {/* Profile Section */}
      <section className="flex flex-col items-center gap-3 pb-6 border-b border-[var(--border-color)]">
        <div className="relative">
          <div className="w-24 h-24 rounded-full border-4 border-[var(--bg-card)] shadow-md overflow-hidden bg-[var(--bg-chip)]">
            <img 
              src={avatarUrl} 
              alt="Avatar" 
              className="w-full h-full object-cover"
              onError={(e) => { 
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="w-full h-full items-center justify-center bg-[var(--bg-header)] text-[var(--accent-sky)]" style={{display: 'none'}}>
              <User size={40} />
            </div>
          </div>
          {/* Photo upload button */}
          <button
            data-testid="btn-change-photo"
            onClick={() => photoInputRef.current?.click()}
            disabled={isUploadingPhoto || !backendAvailable}
            className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-[var(--accent-primary)] text-white flex items-center justify-center shadow-lg hover:opacity-90 active:scale-95 transition-all disabled:opacity-50"
          >
            {isUploadingPhoto ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Camera size={16} />
            )}
          </button>
          <input
            ref={photoInputRef}
            data-testid="input-photo-upload"
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handlePhotoUpload}
            className="hidden"
          />
        </div>
        <div className="text-center">
          <h2 className="text-lg font-bold text-[var(--text-primary)]">
            {localProfile.name || 'Seu Nome'}
          </h2>
          <p className="text-xs text-[var(--text-secondary)]">Vendedor · MA</p>
          {!backendAvailable && (
            <p className="text-xs text-amber-500 mt-1">Offline - foto será salva localmente</p>
          )}
        </div>
      </section>

      {/* Profile Fields */}
      <section className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">Perfil</h3>
        
        <div className="flex items-center gap-3">
          <User size={18} className="text-[var(--text-muted)]" />
          <input
            data-testid="input-profile-name"
            type="text"
            value={localProfile.name}
            onChange={(e) => handleProfileChange('name', e.target.value)}
            placeholder="Seu nome"
            className="flex-1 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg p-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--border-focus)] focus:ring-1 focus:ring-[var(--border-focus)] placeholder:text-[var(--text-muted)]"
          />
        </div>

        <div className="flex items-center gap-3">
          <Building2 size={18} className="text-[var(--text-muted)]" />
          <input
            data-testid="input-profile-company"
            type="text"
            value={localProfile.company}
            onChange={(e) => handleProfileChange('company', e.target.value)}
            placeholder="Empresa"
            className="flex-1 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg p-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--border-focus)] focus:ring-1 focus:ring-[var(--border-focus)] placeholder:text-[var(--text-muted)]"
          />
        </div>

        <div className="flex items-center gap-3">
          <Phone size={18} className="text-[var(--text-muted)]" />
          <input
            data-testid="input-profile-phone"
            type="tel"
            value={localProfile.phone}
            onChange={(e) => handlePhoneChange(e.target.value)}
            placeholder="(555) 123-4567"
            maxLength={14}
            className="flex-1 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg p-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--border-focus)] focus:ring-1 focus:ring-[var(--border-focus)] placeholder:text-[var(--text-muted)]"
          />
        </div>

        <div className="flex items-center gap-3">
          <Mail size={18} className="text-[var(--text-muted)]" />
          <input
            data-testid="input-profile-email"
            type="email"
            value={localProfile.email}
            onChange={(e) => handleProfileChange('email', e.target.value)}
            placeholder="E-mail"
            className="flex-1 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg p-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--border-focus)] focus:ring-1 focus:ring-[var(--border-focus)] placeholder:text-[var(--text-muted)]"
          />
        </div>
      </section>

      {/* Sync Section */}
      <section className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2">
          {backendAvailable ? <Cloud size={16} className="text-green-500" /> : <CloudOff size={16} className="text-[var(--text-muted)]" />}
          Cloud Sync
        </h3>

        {/* Sync Status */}
        <div className="flex items-center justify-between bg-[var(--bg-card)] border border-[var(--border-color)] rounded-lg p-3">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${backendAvailable ? 'bg-green-500/20' : 'bg-[var(--bg-chip)]'}`}>
              {syncStatus === 'syncing' ? (
                <RefreshCw size={20} className="text-blue-500 animate-spin" />
              ) : backendAvailable ? (
                <Cloud size={20} className="text-green-500" />
              ) : (
                <CloudOff size={20} className="text-[var(--text-muted)]" />
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-[var(--text-primary)]">
                {syncStatus === 'saving' ? 'Saving...' : 
                 syncStatus === 'saved' ? 'Saved ✓' :
                 syncStatus === 'error' ? 'Save error' :
                 backendAvailable ? 'Auto-save active' : 'Offline'}
              </p>
              <p className="text-xs text-[var(--text-muted)]">
                {lastSyncTime ? `Last save: ${new Date(lastSyncTime).toLocaleString('en-US')}` : 'Your data is saved automatically'}
              </p>
            </div>
          </div>
        </div>

        {/* User info */}
        {user && (
          <div className="bg-[var(--bg-chip)] rounded-lg p-3 border border-[var(--border-color)]">
            <p className="text-xs text-[var(--text-muted)] mb-1">Logged in as</p>
            <p className="text-sm font-medium text-[var(--text-primary)]">{user.email}</p>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              Your data syncs automatically across all devices
            </p>
          </div>
        )}

        {/* Last Login Location */}
        {user?.lastLogin && (
          <div className="bg-[var(--bg-card)] rounded-lg p-3 border border-[var(--border-color)]">
            <div className="flex items-center gap-2 mb-2">
              <MapPin size={16} className="text-[var(--accent-primary)]" />
              <p className="text-xs font-medium text-[var(--text-primary)]">Last Login</p>
            </div>
            <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
              <Globe size={12} />
              <span>
                {user.lastLogin.city && user.lastLogin.region 
                  ? `${user.lastLogin.city}, ${user.lastLogin.region}` 
                  : user.lastLogin.city || user.lastLogin.country || 'Unknown location'}
                {user.lastLogin.country && ` - ${user.lastLogin.country}`}
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs text-[var(--text-muted)] mt-1">
              <Clock size={12} />
              <span>{new Date(user.lastLogin.timestamp).toLocaleString('en-US')}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-[var(--text-muted)] mt-1">
              <span className="font-mono">IP: {user.lastLogin.ip}</span>
            </div>
          </div>
        )}

        {/* Login History */}
        {user?.loginHistory && user.loginHistory.length > 1 && (
          <details className="bg-[var(--bg-card)] rounded-lg border border-[var(--border-color)]">
            <summary className="p-3 cursor-pointer text-xs font-medium text-[var(--text-primary)] flex items-center gap-2">
              <Clock size={14} />
              Login History ({user.loginHistory.length})
            </summary>
            <div className="px-3 pb-3 space-y-2">
              {user.loginHistory.slice().reverse().map((login) => (
                <div key={`login_${login.timestamp}_${login.ip}`} className="text-xs border-t border-[var(--border-color)] pt-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--text-secondary)]">
                      {login.city || 'Local'}{login.region ? `, ${login.region}` : ''}
                    </span>
                    <span className="text-[var(--text-muted)] font-mono text-[10px]">{login.ip}</span>
                  </div>
                  <span className="text-[var(--text-muted)]">
                    {new Date(login.timestamp).toLocaleString('en-US')}
                  </span>
                </div>
              ))}
            </div>
          </details>
        )}

        {/* Logout button */}
        <button
          data-testid="btn-logout"
          onClick={handleLogout}
          className="w-full h-11 bg-[var(--bg-card)] border border-[var(--border-color)] text-[var(--text-secondary)] rounded-lg flex items-center justify-center gap-2 active:bg-[var(--bg-chip)] font-medium text-sm"
        >
          <LogOut size={16} /> Sair da Conta
        </button>
      </section>

      {/* Map Tools Section */}
      <section className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2">
          <Navigation size={16} /> Map Tools
        </h3>
        
        <div className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-lg p-4">
          <div className="flex items-start gap-3 mb-3">
            <MapPin size={20} className="text-[var(--accent-primary)] mt-0.5" />
            <div>
              <p className="text-sm font-medium text-[var(--text-primary)]">Re-geocode All Stores</p>
              <p className="text-xs text-[var(--text-secondary)] mt-1">
                Update GPS coordinates for all stores based on their addresses. 
                This fixes stores that appear in the wrong location on the map.
              </p>
            </div>
          </div>
          
          <button
            data-testid="btn-regeocode-all"
            onClick={handleRegeocodeAll}
            disabled={isGeocoding || lojas.length === 0}
            className="w-full h-11 bg-[var(--accent-primary)] text-white rounded-lg flex items-center justify-center gap-2 active:opacity-90 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGeocoding ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Geocoding... ({geocodeProgress.current}/{geocodeProgress.total})
              </>
            ) : (
              <>
                <RefreshCw size={18} /> Re-geocode All Stores ({lojas.filter(l => l.address && l.city).length})
              </>
            )}
          </button>
        </div>
      </section>

      {/* Appearance Section */}
      <section className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2">
          {isDark ? <Moon size={16} /> : <Sun size={16} />} Appearance
        </h3>
        
        <div className="flex items-center justify-between bg-[var(--bg-card)] border border-[var(--border-color)] rounded-lg p-3">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-indigo-900/50' : 'bg-amber-100'}`}>
              {isDark ? <Moon size={20} className="text-indigo-300" /> : <Sun size={20} className="text-amber-600" />}
            </div>
            <div>
              <p className="text-sm font-medium text-[var(--text-primary)]">
                {isDark ? 'Dark Mode' : 'Light Mode'}
              </p>
              <p className="text-xs text-[var(--text-muted)]">
                {isDark ? 'Easy on the eyes' : 'Classic bright look'}
              </p>
            </div>
          </div>
          <button
            data-testid="btn-toggle-theme"
            onClick={toggleTheme}
            className={`relative w-14 h-8 rounded-full transition-colors ${
              isDark ? 'bg-indigo-600' : 'bg-gray-300'
            }`}
          >
            <span 
              className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow-md transition-transform ${
                isDark ? 'translate-x-7' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </section>

      {/* Visit Reminders Section */}
      <section className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2">
          <Bell size={16} /> Visit Reminders
        </h3>

        {notificationStatus === 'not_supported' ? (
          /* Browser doesn't support notifications */
          <div className="bg-[var(--bg-chip)] border border-[var(--border-color)] rounded-lg p-4">
            <div className="flex items-start gap-3">
              <BellOff size={20} className="text-[var(--text-muted)] mt-0.5" />
              <div>
                <p className="text-sm font-medium text-[var(--text-primary)]">Notifications not supported</p>
                <p className="text-xs text-[var(--text-secondary)] mt-1">
                  Your browser does not support push notifications. Try using Chrome, Firefox or Safari.
                </p>
              </div>
            </div>
          </div>
        ) : notificationStatus === 'denied' ? (
          /* Notifications blocked by user */
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="text-red-500 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-500">Notifications blocked</p>
                <p className="text-xs text-red-400 mt-1">
                  You have blocked notifications for this site. To enable reminders, update your browser settings.
                </p>
                <div className="mt-3 flex flex-col gap-2">
                  <p className="text-xs text-[var(--text-secondary)] font-medium">How to enable:</p>
                  <ol className="text-xs text-[var(--text-muted)] list-decimal list-inside space-y-1">
                    <li>Click the lock icon in the address bar</li>
                    <li>Find Notifications permission</li>
                    <li>Change from Block to Allow</li>
                    <li>Refresh this page</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        ) : !notificationsGranted ? (
          /* Permission not yet requested (default state) */
          <div className="flex flex-col gap-3">
            <button
              data-testid="btn-enable-notifications"
              onClick={handleEnableNotifications}
              className="w-full h-11 bg-amber-600 text-white rounded-lg flex items-center justify-center gap-2 active:bg-amber-700 font-medium text-sm"
            >
              <Bell size={18} /> Enable Notifications
            </button>
            <p className="text-xs text-[var(--text-muted)] text-center">
              Get reminded when stores need a visit
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between bg-[var(--bg-card)] border border-[var(--border-color)] rounded-lg p-3">
              <div className="flex items-center gap-2">
                {reminderSettings.enabled ? (
                  <Bell size={18} className="text-amber-500" />
                ) : (
                  <BellOff size={18} className="text-[var(--text-muted)]" />
                )}
                <span className="text-sm text-[var(--text-primary)]">
                  {reminderSettings.enabled ? 'Reminders enabled' : 'Reminders disabled'}
                </span>
              </div>
              <button
                data-testid="btn-toggle-reminders"
                onClick={handleToggleReminders}
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  reminderSettings.enabled 
                    ? 'bg-amber-500/20 text-amber-500' 
                    : 'bg-[var(--bg-chip)] text-[var(--text-muted)]'
                }`}
              >
                {reminderSettings.enabled ? 'ON' : 'OFF'}
              </button>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-sm text-[var(--text-secondary)]">Remind after</span>
              <select
                data-testid="select-reminder-days"
                value={reminderSettings.days}
                onChange={(e) => handleReminderDaysChange(e.target.value)}
                className="flex-1 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg p-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--border-focus)]"
              >
                <option value="7">7 days</option>
                <option value="14">14 days</option>
                <option value="30">30 days</option>
                <option value="60">60 days</option>
                <option value="90">90 days</option>
              </select>
              <span className="text-sm text-[var(--text-secondary)]">without visit</span>
            </div>

            {overdueStores.length > 0 && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <p className="text-sm text-amber-500 font-medium">
                  ⚠️ {overdueStores.length} store{overdueStores.length !== 1 ? 's' : ''} need{overdueStores.length === 1 ? 's' : ''} a visit!
                </p>
                <ul className="mt-2 text-xs text-amber-400">
                  {overdueStores.slice(0, 3).map(store => (
                    <li key={store.id}>
                      • {store.name} ({store.daysSinceVisit ? `${store.daysSinceVisit} days ago` : 'never visited'})
                    </li>
                  ))}
                  {overdueStores.length > 3 && (
                    <li>• ... and {overdueStores.length - 3} more</li>
                  )}
                </ul>
              </div>
            )}
          </>
        )}
      </section>

      {/* Export Section */}
      <section className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">Exportar</h3>
        
        <button
          data-testid="btn-export-csv"
          onClick={handleExportCSV}
          className="w-full h-11 bg-[var(--accent-primary)] text-white rounded-lg flex items-center justify-center gap-2 active:opacity-90 font-medium text-sm"
        >
          <FileSpreadsheet size={18} /> Exportar para Excel (.csv)
        </button>

        <button
          data-testid="btn-export-json"
          onClick={handleExportJSON}
          className="w-full h-11 bg-[var(--bg-header)] text-white rounded-lg flex items-center justify-center gap-2 active:opacity-90 font-medium text-sm"
        >
          <Download size={18} /> Export to JSON
        </button>

        <button
          data-testid="btn-copy-json"
          onClick={handleCopyJSON}
          className="w-full h-11 bg-[var(--bg-chip)] text-[var(--text-primary)] rounded-lg flex items-center justify-center gap-2 active:opacity-80 font-medium text-sm border border-[var(--border-color)]"
        >
          <Copy size={18} /> Copy raw data
        </button>
      </section>

      {/* Import Section */}
      <section className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">Import</h3>
        
        <label className="w-full h-11 bg-green-600 text-white rounded-lg flex items-center justify-center gap-2 active:bg-green-700 font-medium text-sm cursor-pointer">
          <Upload size={18} /> Import JSON
          <input
            data-testid="input-import-json"
            type="file"
            accept=".json"
            onChange={handleImportJSON}
            className="hidden"
          />
        </label>
      </section>

      {/* Danger Zone */}
      <section className="p-4 border border-red-500/30 bg-red-500/10 rounded-xl">
        <h3 className="text-sm font-semibold text-red-500 mb-3">Danger Zone</h3>
        
        <button
          data-testid="btn-danger-delete-all"
          onClick={handleDeleteAll}
          className="w-full h-11 bg-red-600 text-white rounded-lg flex items-center justify-center gap-2 active:bg-red-700 font-medium text-sm"
        >
          <Trash2 size={18} /> Delete all data
        </button>
      </section>
    </div>
  );
}
